import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Button,
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead, MenuItem,
  TableRow, Select, FormControl,
  InputLabel, IconButton,
  TextField, TablePagination, Divider,
  Paper, FormControlLabel, Radio, RadioGroup
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import axios, { renderDoc, decryptData } from '../Axiosinstance';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

const ModalTable = ({ open, handleClose, data, externalStudents, answers, assessments, fetchAnswers }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [grading, setGrading] = useState("");
  const [score, setScore] = useState(null);
  const [comments, setComments] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const approver = decryptData(localStorage.getItem('name'));
  const [loading, setLoading] = useState(false);

  // Handle score input, clear grading if score is enteredF
  const handleScoreChange = (event) => {
    let value = event.target.value;

    // Allow only numbers and limit to 100
    if (value === "" || (/^\d+(\.\d{0,2})?$/.test(value) && Number(value) <= 100)) {
      setScore(value === "" ? null : Number(value));
      setGrading(""); // Clear grading when score is entered
    }
  };

  // Ensure data.moduleName exists before filtering
  if (!data || !data.moduleName) {
    console.error("Error: data.moduleName is undefined!");
    return null; // Prevents rendering if data is invalid
  }

  // Fetch assessments for the current module only
  const moduleAssessments = assessments.filter(assessment => assessment.moduleName === data.moduleName);

  // If there are no assessments for the module, do not render student rows
  if (moduleAssessments.length === 0) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          height: "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          overflow: "auto",
        }}>
          <Typography variant="h6">No assessments available for this module.</Typography>
          <Button onClick={handleClose} variant="contained" sx={{ mt: 2 }}>Close</Button>
        </Box>
      </Modal>
    );
  }

  // Combine external students with their completion status, file URL, and assessments
  const studentsWithStatus = externalStudents.map(student => {
    const studentAnswers = answers.filter(answer => answer.studentId === student.student.id && answer.moduleName === data.moduleName);

    // Create a list of assessments with their descriptions and status for the current student
    const studentAssessments = moduleAssessments.map(assessment => {
      const assessmentAnswer = studentAnswers.find(answer => answer.trentAssessmentId === assessment.id);
      return {
        description: assessment.description,
        status: assessmentAnswer ? "Completed" : "Not Completed",
        submittedAt: assessmentAnswer ? assessmentAnswer.submittedAt : null, // Get submitted date if available
         url: assessmentAnswer?.url || assessment.url || null,
        grade: assessmentAnswer ? assessmentAnswer.grade : null, // Get URL if available
        comments: assessmentAnswer ? assessmentAnswer.comments : null,
        id: assessmentAnswer ? assessmentAnswer.id : null,
      };
    });

    return {
      student: student.name,
      module: data.moduleName,
      assessments: studentAssessments,
    };
  });

  // console.log("hello",studentsWithStatus)
  // Apply search and filter (if needed)
  const filteredData = studentsWithStatus.filter(row =>
    row.student.toLowerCase().includes(search.toLowerCase())
  );




  const handleSave = async () => {
    setLoading(true); // Start loader
    try {
      const response = await axios.put(`/submissions/update/${selectedId}`, {
        moduleName: moduleName,
        grade: score,
        comments: comments,
        approver: approver,
      });

      console.log("Submission updated successfully:", response.data);
      toast.success("Mark updated successfully");
    } catch (error) {
      console.error("Error updating submission:", error);
    } finally {
      setLoading(false); // Stop loader
      setFileModalOpen(false); // Close modal after saving
      fetchAnswers();
    }
  };


  const handleViewClick = (assessment, row) => {
    setModuleName(row.module);
    setSelectedFile(assessment.url);
    setSelectedId(assessment.id);
    setScore(assessment.grade || 0); // Set score from assessment, default to 0 if not available
    setComments(assessment.comments || ""); // Set comments from assessment
    setFileModalOpen(true); // Open modal
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "90%",
        height: "80%",
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 4,
        overflow: "auto",
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Batch Details</Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </Box>

        <Box display="flex" gap={2} mb={2}>
          <TextField label="Search by Module"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)} />
          <FormControl fullWidth>
            <InputLabel>Status Filter</InputLabel>
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Status Filter">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Not Completed">Not Completed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell style={{ fontWeight: 'bold'}} >Student Name</TableCell>
                <TableCell style={{ fontWeight: 'bold'}} >Module Name</TableCell>
                <TableCell style={{ fontWeight: 'bold'}} >Assessment Description</TableCell>
                <TableCell style={{ fontWeight: 'bold'}} >Status</TableCell>
                <TableCell style={{ fontWeight: 'bold'}} >Submitted At</TableCell>
                <TableCell style={{ fontWeight: 'bold'}} >Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.filter(row => filter === "" || row.assessments.some(assessment => assessment.status === filter))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                    row.assessments.map((assessment, assessmentIndex) => (
                      <TableRow key={`${index}-${assessmentIndex}`}>
                        {assessmentIndex === 0 && <TableCell rowSpan={row.assessments.length}>{row.student}</TableCell>}
                        <TableCell>{row.module}</TableCell>
                        <TableCell>{assessment.description}</TableCell>
                        <TableCell>{assessment.status}</TableCell>
                        <TableCell>{assessment.submittedAt ? new Date(assessment.submittedAt).toLocaleString() : 'N/A'}</TableCell>
                        <TableCell>
                          {assessment.url && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleViewClick(assessment, row)}>
                              View
                            </Button>

                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No data available</TableCell>
                </TableRow>
              )}
            </TableBody>


            <Modal open={fileModalOpen} onClose={() => setFileModalOpen(false)}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "90%", sm: "80%" },
                  height: { xs: "90%" },
                  bgcolor: "white",
                  boxShadow: 24,
                  p: { xs: 2, md: 4 },
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>

                  <Typography variant="h4" fontWeight="bold" color="primary">
                    Grading & File Preview
                  </Typography> 


                  <Divider sx={{ mb: 2 }} />

                  <IconButton onClick={() => setFileModalOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    flexGrow: 1,
                    gap: 2,
                    overflowY: "auto"
                  }}
                >
                  {/* Left - Grading, Score & Comments */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      borderRight: { md: "1px solid #ccc" },
                      borderBottom: { xs: "1px solid #ccc", md: "none" }
                    }}
                  >


                    {/* Score Input */}
                    <Typography variant="h5" sx={{ mt: 2, color: 'black' }}>
                      Enter Mark (out of 100)
                    </Typography>
                    <TextField
                      type="text"
                      fullWidth
                      value={score}
                      onChange={handleScoreChange}
                      placeholder="Enter score (e.g., 85%)"
                      disabled={grading !== ""}
                      inputProps={{ pattern: "[0-9]*", inputMode: "numeric", maxLength: 5 }}
                      sx={{ mt: 1 }}
                    />

                    {/* Comments Section */}
                    <Typography variant="h5" sx={{ mt: 2, color: 'black' }}>
                      Comments
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Enter comments..."
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {/* Right - File Preview */}
                  <Box sx={{ flex: 2, p: 2 }}>
                    <Typography variant="h5" sx={{ color: 'black' }} gutterBottom>
                      File Preview
                    </Typography>
                    {selectedFile ? (
                      <iframe
                        src={selectedFile}
                        width="100%"
                        height="800px"
                        title="File Viewer"
                        style={{ border: "1px solid #ccc", borderRadius: "8px" }}
                      />
                    ) : (
                      <Typography>No file available</Typography>
                    )}
                  </Box>
                </Box>

                {/* Bottom - Save Button */}
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading || (!grading && !score)}
                    sx={{ px: 3, py: 1, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                  </Button>
                </Box>
              </Box>
            </Modal>


          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 15]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
        />
        <Button onClick={handleClose} variant="contained" sx={{ mt: 2 }}>Close</Button>
      </Box>
    </Modal>
  );
};




const Module = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [externalStudents, setExternalStudents] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [assessments, setAssessments] = useState([]);


  const { batchId, intakeId } = useParams();
  const batchId1 = decryptData(decodeURIComponent(batchId))
  const intakeId1 = decryptData(decodeURIComponent(intakeId))

  const handleOpen = (index) => {
    setOpenIndex(index);
    fetchAssessments(batchDetails.moduleDetails[index].moduleName); // Fetch assessments for the selected module
  };

  const handleClose = () => setOpenIndex(null);

  const fetchAssessments = async (moduleName) => {
    try {
      const response = await axios.get(`trent_assessments/${batchId1}/${moduleName}`);
      setAssessments(response.data);
    } catch (error) {
      console.error("Error fetching assessments:", error);
    }
  };

  const fetchBatchDetails = async () => {
    try {
      const response = await axios.get(`batch/${batchId1}`);
      setBatchDetails(response.data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
    }
  };

  const fetchStudentsByIntake = async () => {
    try {
      const response = await axios.get(`/registrationadvances/active/${intakeId1}`);
      const externalData = response.data;
      setExternalStudents(externalData);
    } catch (error) {
      console.error('Error fetching external data:', error);
    }
  };


  const fetchAnswers = async () => {
    try {
      const response = await axios.get(`submissions/intake/${intakeId1}`);
      setAnswers(response.data);
    } catch (error) {
      console.error("Error fetching assessments:", error);
    }
  };

  useEffect(() => {

    fetchAnswers();
    fetchBatchDetails();
    fetchStudentsByIntake();
  }, [batchId1, intakeId1]);

  return (
    <Container>
      <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
        {batchDetails?.moduleDetails?.map((module, index) => (
          <Grid item xs={12} sm={6} md={4} key={module.id}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                width: "100%", // Ensures all buttons take full width of the grid
                minHeight: "80px", // Keeps all buttons at least this tall
                maxHeight: "120px", // Prevents excessive growth
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                fontSize: "1rem",
                padding: "10px",
                whiteSpace: "normal", // Allows text to wrap
                wordBreak: "break-word", // Breaks words if necessary
              }}
              onClick={() => handleOpen(index)}
            >
              {module.moduleName}
            </Button>
          </Grid>

        ))}
      </Grid>
      {openIndex !== null && (
        <ModalTable
          open={true}
          handleClose={handleClose}
          data={batchDetails?.moduleDetails[openIndex]}
          externalStudents={externalStudents}
          answers={answers}
          assessments={assessments}
          fetchAnswers={fetchAnswers}
        />
      )}
      <ToastContainer />
    </Container>
  );
};

export default Module;
